import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useCurrency } from '../context/CurrencyContext';
import TrainingGuideEditor from '../components/TrainingGuideEditor';
import AdminTrainingGuides from '../components/AdminTrainingGuides';
import AdminFeatureFlags from '../components/AdminFeatureFlags';
import { resolveImageUrl } from '../utils/api';
import { compressImage } from '../utils/compressImage';
import {
  putPendingBlob,
  getPendingBlob,
  deletePendingBlobs,
  saveDraft,
  loadDraft,
  clearDraft,
} from '../utils/draftStorage';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

// Couriers commonly used for Malaysian domestic parcels. "Other" reveals a free
// text field so the list never blocks a shipment.
const COURIERS = [
  'J&T Express',
  'Pos Laju',
  'Ninja Van',
  'City-Link Express',
  'DHL eCommerce',
  'GDEX',
  'Skynet',
  'Flash Express',
  'Shopee Express',
  'Lalamove',
  'Other',
];

const formatBytes = (bytes) => `${(bytes / 1024 / 1024).toFixed(1)} MB`;

const ADMIN_VIEW_KEY = 'doctape:adminView';

const readSavedView = () => {
  try {
    return JSON.parse(localStorage.getItem(ADMIN_VIEW_KEY) || 'null');
  } catch {
    return null; // corrupt entry must not take down the admin page
  }
};

export default function AdminPage() {
  // Read during the first render rather than in an effect. An effect-based
  // restore has to defend against writing the default 'dashboard' back over
  // the saved tab in the window before it runs — which is exactly the bug this
  // avoids by never having that window.
  const savedView = React.useRef(readSavedView()).current;

  const [activeTab, setActiveTab] = useState(savedView?.activeTab || 'dashboard');
  const [editingProduct, setEditingProduct] = useState(null);
  const [editingProductId, setEditingProductId] = useState(savedView?.editingProductId || null);
  const [editingGuide, setEditingGuide] = useState(null);

  // The product itself is re-fetched rather than stored, so it cannot go stale.
  // Only the id is persisted, and it is held in its own state so the write
  // below stays correct while the fetch is still in flight.
  React.useEffect(() => {
    if (!editingProductId || editingProduct?._id === editingProductId) return;
    let cancelled = false;

    (async () => {
      try {
        const res = await fetch(`${API_BASE}/products/${editingProductId}`);
        if (cancelled) return;
        if (res.ok) {
          setEditingProduct(await res.json());
        } else {
          setEditingProductId(null); // deleted meanwhile
          setActiveTab('products');
        }
      } catch {
        if (!cancelled) setEditingProductId(null);
      }
    })();

    return () => { cancelled = true; };
  }, [editingProductId, editingProduct]);

  React.useEffect(() => {
    try {
      localStorage.setItem(ADMIN_VIEW_KEY, JSON.stringify({ activeTab, editingProductId }));
    } catch {
      /* storage unavailable; the view simply will not be restored */
    }
  }, [activeTab, editingProductId]);

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setEditingProductId(product._id);
    setActiveTab('add-product');
  };

  const handleProductSaved = () => {
    setEditingProduct(null);
    setEditingProductId(null);
    setActiveTab('products');
  };

  const handleEditGuide = (guide) => {
    setEditingGuide(guide);
    setActiveTab('edit-guide');
  };

  const handleGuideCreated = () => {
    setEditingGuide(null);
    setActiveTab('training-guides');
  };

  return (
    <div className="h-screen bg-white overflow-hidden flex flex-col">
      {/* Admin Header */}
      <div className="bg-black text-white p-8 border-b border-white flex-shrink-0">
        <div className="w-4/5 mx-auto flex justify-between items-center">
          <h1 className="text-4xl font-light tracking-tight">Admin Dashboard</h1>
          <Link to="/" className="bg-white text-black hover:bg-gray-100 px-8 py-3 font-light tracking-wide transition-all duration-300">
            ← Back to Store
          </Link>
        </div>
      </div>

      {/* Admin Navigation */}
      <div className="bg-white border-b border-gray-200 flex-shrink-0">
        <div className="w-4/5 mx-auto flex gap-12 p-6">
          <button
            onClick={() => setActiveTab('dashboard')}
            className={`py-3 px-2 font-light tracking-wide border-b-2 transition-all ${
              activeTab === 'dashboard'
                ? 'border-black text-black'
                : 'border-transparent text-gray-600 hover:text-black'
            }`}
          >
            Dashboard
          </button>
          <button
            onClick={() => setActiveTab('products')}
            className={`py-3 px-2 font-light tracking-wide border-b-2 transition-all ${
              activeTab === 'products'
                ? 'border-black text-black'
                : 'border-transparent text-gray-600 hover:text-black'
            }`}
          >
            Products
          </button>
          <button
            onClick={() => {
              setEditingProduct(null);
              setActiveTab('add-product');
            }}
            className={`py-3 px-2 font-light tracking-wide border-b-2 transition-all ${
              activeTab === 'add-product'
                ? 'border-black text-black'
                : 'border-transparent text-gray-600 hover:text-black'
            }`}
          >
            {editingProduct ? 'Edit Product' : 'Add Product'}
          </button>
          <button
            onClick={() => setActiveTab('orders')}
            className={`py-3 px-2 font-light tracking-wide border-b-2 transition-all ${
              activeTab === 'orders'
                ? 'border-black text-black'
                : 'border-transparent text-gray-600 hover:text-black'
            }`}
          >
            Orders
          </button>
          <button
            onClick={() => setActiveTab('training-guides')}
            className={`py-3 px-2 font-light tracking-wide border-b-2 transition-all ${
              activeTab === 'training-guides'
                ? 'border-black text-black'
                : 'border-transparent text-gray-600 hover:text-black'
            }`}
          >
            Training Guides
          </button>
          <button
            onClick={() => setActiveTab('feature-flags')}
            className={`py-3 px-2 font-light tracking-wide border-b-2 transition-all ${
              activeTab === 'feature-flags'
                ? 'border-black text-black'
                : 'border-transparent text-gray-600 hover:text-black'
            }`}
          >
            Feature Flags
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className={`py-3 px-2 font-light tracking-wide border-b-2 transition-all ${
              activeTab === 'users'
                ? 'border-black text-black'
                : 'border-transparent text-gray-600 hover:text-black'
            }`}
          >
            Users
          </button>
        </div>
      </div>

      {/* Admin Content */}
      <div className="flex-grow overflow-hidden w-4/5 mx-auto p-8">
        <div className="h-full overflow-y-auto">
          {activeTab === 'dashboard' && <AdminDashboard />}
          {activeTab === 'products' && <AdminProducts onEditProduct={handleEditProduct} />}
          {activeTab === 'add-product' && <AdminAddProduct editingProduct={editingProduct} onProductSaved={handleProductSaved} />}
          {activeTab === 'orders' && <AdminOrders />}
          {activeTab === 'training-guides' && <AdminTrainingGuides onEditGuide={handleEditGuide} onCreateNew={() => {
            setEditingGuide(null);
            setActiveTab('edit-guide');
          }} />}
          {activeTab === 'edit-guide' && <TrainingGuideEditor editingGuide={editingGuide} onSave={handleGuideCreated} />}
          {activeTab === 'feature-flags' && <AdminFeatureFlags />}
          {activeTab === 'users' && <AdminUsers />}
        </div>
      </div>
    </div>
  );
}

function AdminDashboard() {
  const [stats, setStats] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const { getCurrencySymbol } = useCurrency();

  React.useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await fetch(`${API_BASE}/users/admin/stats`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        if (response.ok) {
          const data = await response.json();
          setStats(data);
        }
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  return (
    <div className="bg-white border border-gray-200 p-8">
      <h2 className="text-3xl font-light mb-12 tracking-tight">Dashboard</h2>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
        <div className="bg-white p-8 border border-gray-200">
          <p className="text-gray-600 text-sm font-light tracking-wide mb-3">Total Products</p>
          <p className="text-4xl font-light text-black">{loading ? '--' : stats?.productCount ?? '--'}</p>
        </div>
        <div className="bg-white p-8 border border-gray-200">
          <p className="text-gray-600 text-sm font-light tracking-wide mb-3">Total Orders</p>
          <p className="text-4xl font-light text-black">{loading ? '--' : stats?.orderCount ?? '--'}</p>
        </div>
        <div className="bg-white p-8 border border-gray-200">
          <p className="text-gray-600 text-sm font-light tracking-wide mb-3">Total Users</p>
          <p className="text-4xl font-light text-black">{loading ? '--' : stats?.userCount ?? '--'}</p>
        </div>
        <div className="bg-white p-8 border border-gray-200">
          <p className="text-gray-600 text-sm font-light tracking-wide mb-3">Total Revenue</p>
          <p className="text-4xl font-light text-black">{loading ? '--' : `${getCurrencySymbol('MYR')}${(stats?.totalRevenue ?? 0).toFixed(2)}`}</p>
        </div>
      </div>
    </div>
  );
}

function AdminProducts({ onEditProduct }) {
  const [products, setProducts] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [togglingId, setTogglingId] = React.useState(null);
  const { getCurrencySymbol } = useCurrency();

  React.useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await fetch(`${API_BASE}/products`);
        const data = await response.json();
        // Ensure data is always an array
        setProducts(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Error fetching products:', error);
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;

    try {
      const response = await fetch(`${API_BASE}/products/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        setProducts(products.filter(p => p._id !== id));
        alert('Product deleted successfully');
      }
    } catch (error) {
      console.error('Error deleting product:', error);
      alert('Failed to delete product');
    }
  };

  const handleToggleListing = async (product) => {
    setTogglingId(product._id);
    try {
      const response = await fetch(`${API_BASE}/products/${product._id}/listing`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          listed: !product.listed,
          listedAt: new Date().toISOString()
        })
      });
      if (response.ok) {
        const updatedProduct = await response.json();
        setProducts(products.map(p => p._id === product._id ? updatedProduct : p));
      }
    } catch (error) {
      console.error('Error toggling listing:', error);
    } finally {
      setTogglingId(null);
    }
  };

  if (loading) return <div className="text-center py-12 font-light text-gray-600">Loading products...</div>;

  return (
    <div className="bg-white border border-gray-200 p-8 h-full flex flex-col overflow-hidden">
      <h2 className="text-3xl font-light mb-8 tracking-tight flex-shrink-0">Products</h2>
      {!Array.isArray(products) || products.length === 0 ? (
        <p className="text-gray-600 font-light">No products found</p>
      ) : (
        <div className="flex-grow overflow-y-auto border border-gray-200 rounded">
          <div className="overflow-x-auto">
            <table className="w-full">
            <thead className="border-b border-gray-200">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-light text-gray-700 tracking-wide">Name</th>
                <th className="px-6 py-4 text-left text-sm font-light text-gray-700 tracking-wide">Category</th>
                <th className="px-6 py-4 text-left text-sm font-light text-gray-700 tracking-wide">Price</th>
                <th className="px-6 py-4 text-left text-sm font-light text-gray-700 tracking-wide">Stock</th>
                <th className="px-6 py-4 text-left text-sm font-light text-gray-700 tracking-wide">Actions</th>
              </tr>
            </thead>
            <tbody>
              {products.map(product => (
                <tr key={product._id} className="border-b border-gray-200 hover:bg-gray-50">
                  <td className="px-6 py-4 text-gray-900 font-light">{product.name}</td>
                  <td className="px-6 py-4 text-gray-700 font-light">{product.category}</td>
                  <td className="px-6 py-4 text-gray-700 font-light">{getCurrencySymbol()}{product.price}</td>
                  <td className="px-6 py-4 text-gray-700 font-light">{product.stock}</td>
                  <td className="px-6 py-4 space-x-2 text-sm">
                    <button 
                      onClick={() => onEditProduct(product)}
                      className="text-black hover:underline font-light"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleToggleListing(product)}
                      disabled={togglingId === product._id}
                      className="text-gray-600 hover:text-black underline font-light"
                    >
                      {togglingId === product._id ? 'Updating...' : (product.listed ? 'Hide' : 'List')}
                    </button>
                    <button
                      onClick={() => handleDelete(product._id)}
                      className="text-gray-600 hover:text-black underline font-light"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}
    </div>
  );
}

function AdminAddProduct({ editingProduct, onProductSaved }) {
  const [formData, setFormData] = React.useState(
    editingProduct ? {
      name: editingProduct.name,
      description: editingProduct.description,
      price: editingProduct.price,
      category: editingProduct.category,
      stock: editingProduct.stock,
      image: editingProduct.image,
      images: editingProduct.images || [],
      variants: editingProduct.variants || []
    } : {
      name: '',
      description: '',
      price: '',
      category: 'Athletic Tapes',
      stock: '',
      image: '',
      images: [],
      variants: []
    }
  );
  const [loading, setLoading] = React.useState(false);
  const [message, setMessage] = React.useState('');
  const [uploadingImage, setUploadingImage] = React.useState(false);
  const [processingImages, setProcessingImages] = React.useState(false);
  const [pending, setPending] = React.useState([]);
  const [draftRestored, setDraftRestored] = React.useState(false);
  const previewUrls = React.useRef({});
  const loadedScope = React.useRef(null);
  const { getCurrencySymbol } = useCurrency();
  const apiBase = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';

  // One draft per target, so an in-progress edit never overwrites a new-product
  // draft (or another product's).
  const draftScope = editingProduct ? `product:${editingProduct._id}` : 'product:new';

  React.useEffect(() => {
    if (editingProduct) {
      setFormData({
        name: editingProduct.name,
        description: editingProduct.description,
        price: editingProduct.price,
        category: editingProduct.category,
        stock: editingProduct.stock,
        image: editingProduct.image,
        images: editingProduct.images || [],
      variants: editingProduct.variants || []
      });
    }
  }, [editingProduct]);

  // Restore a draft left behind by a refresh, rebuilding previews from the
  // blobs kept in IndexedDB. Runs once per target.
  React.useEffect(() => {
    let cancelled = false;
    // Cleared synchronously so the save effect below cannot persist this
    // form's values against a scope whose draft has not been read yet.
    loadedScope.current = null;

    (async () => {
      const draft = loadDraft(draftScope);

      const restoredPending = [];
      for (const item of draft?.pending || []) {
        const blob = await getPendingBlob(item.id);
        if (!blob) continue; // evicted; skip rather than show a broken thumbnail
        previewUrls.current[item.id] = URL.createObjectURL(blob);
        restoredPending.push(item);
      }
      if (cancelled) return;

      if (draft?.formData) {
        setFormData(draft.formData);
        setPending(restoredPending);
        setDraftRestored(true);
      }
      loadedScope.current = draftScope;
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftScope]);

  // Persist on every change. Blobs are already in IndexedDB; this stores the
  // field values and the pending-file metadata that references them.
  React.useEffect(() => {
    if (loadedScope.current !== draftScope) return;
    saveDraft(draftScope, { formData, pending });
  }, [draftScope, formData, pending]);

  // Object URLs are per-document; release them when this form goes away.
  React.useEffect(() => {
    const urls = previewUrls.current;
    return () => {
      Object.values(urls).forEach(URL.revokeObjectURL);
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Files are held locally and only sent to storage on submit, so abandoning
  // the form never leaves orphaned objects in the bucket. A pending image is
  // referenced as "pending:<id>" until it has a real URL.
  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setMessage('');

    setProcessingImages(true);
    const accepted = [];
    const shrunk = [];
    const failed = [];

    for (const original of files) {
      let file = original;
      try {
        const result = await compressImage(original, { maxBytes: MAX_IMAGE_BYTES });
        file = result.file;
        if (result.compressed) {
          shrunk.push(`${original.name} ${formatBytes(result.originalSize)} → ${formatBytes(file.size)}`);
        }
      } catch {
        failed.push(original.name);
        continue;
      }

      const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      await putPendingBlob(id, file);
      accepted.push({ id, name: file.name, type: file.type });
      previewUrls.current[id] = URL.createObjectURL(file);
    }

    e.target.value = '';
    setProcessingImages(false);

    if (accepted.length) {
      setPending(prev => [...prev, ...accepted]);
      setFormData(prev => ({
        ...prev,
        image: prev.image || `pending:${accepted[0].id}`,
      }));
    }

    if (failed.length) {
      setMessage(`✗ Could not process: ${failed.join(', ')}`);
    } else if (shrunk.length) {
      setMessage(`✓ ${accepted.length} image${accepted.length > 1 ? 's' : ''} ready · compressed ${shrunk.join(', ')}`);
    } else if (accepted.length) {
      setMessage(`✓ ${accepted.length} image${accepted.length > 1 ? 's' : ''} ready — saved when you submit`);
    }
  };

  // ── Variants ────────────────────────────────────────────────────────────────
  // Optional. Once a product has variants it is sold only through them, and its
  // own price/stock fields become derived summaries maintained by the server.
  const addVariant = () => {
    setFormData(prev => ({
      ...prev,
      variants: [...(prev.variants || []), { label: '', price: '', stock: '' }],
    }));
  };

  const updateVariant = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      variants: prev.variants.map((v, i) => i === index ? { ...v, [field]: value } : v),
    }));
  };

  const removeVariant = (index) => {
    setFormData(prev => ({
      ...prev,
      variants: prev.variants.filter((_, i) => i !== index),
    }));
  };

  const removeImage = (ref) => {
    if (ref.startsWith('pending:')) {
      const id = ref.slice('pending:'.length);
      if (previewUrls.current[id]) {
        URL.revokeObjectURL(previewUrls.current[id]);
        delete previewUrls.current[id];
      }
      deletePendingBlobs([id]);
      setPending(prev => prev.filter(p => p.id !== id));
    } else {
      setFormData(prev => ({ ...prev, images: prev.images.filter(u => u !== ref) }));
    }

    // Reassign the primary if it was the one removed.
    setFormData(prev => {
      if (prev.image !== ref) return prev;
      const remainingUploaded = prev.images.filter(u => u !== ref);
      const remainingPending = pending
        .filter(p => `pending:${p.id}` !== ref)
        .map(p => `pending:${p.id}`);
      return { ...prev, image: remainingUploaded[0] || remainingPending[0] || '' };
    });
  };

  const setPrimary = (ref) => {
    setFormData(prev => ({ ...prev, image: ref }));
  };

  const hasVariants = (formData.variants || []).length > 0;
  const variantPrices = hasVariants ? formData.variants.map(v => parseFloat(v.price) || 0) : [];
  const variantStockTotal = hasVariants
    ? formData.variants.reduce((sum, v) => sum + (parseInt(v.stock, 10) || 0), 0)
    : 0;

  // Uploaded URLs first, then not-yet-saved files, in selection order.
  const galleryItems = [
    ...(formData.images || []).map(url => ({ ref: url, src: resolveImageUrl(url), pending: false })),
    ...pending.map(p => ({ ref: `pending:${p.id}`, src: previewUrls.current[p.id], pending: true })),
  ];

  // The primary may still be a pending file, which has no URL yet.
  const primaryPreviewSrc =
    galleryItems.find(item => item.ref === formData.image)?.src ||
    (formData.image && !formData.image.startsWith('pending:') ? resolveImageUrl(formData.image) : '');

  const deleteUploaded = (keys) =>
    Promise.all(
      keys.map(key =>
        fetch(`${apiBase}/uploads/${key}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        }).catch(() => {})
      )
    );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    // Tracks what reached storage during this submit, so a later failure can
    // undo it instead of stranding objects in the bucket.
    const uploadedKeys = [];

    try {
      const token = localStorage.getItem('token');
      const uploadedByPendingId = {};

      if (pending.length) {
        setUploadingImage(true);
        for (const item of pending) {
          const blob = await getPendingBlob(item.id);
          if (!blob) throw new Error(`"${item.name}" is no longer available — please re-select it`);

          // Rebuild as a File so the request carries the original name and
          // MIME type — the server's fileFilter rejects anything else.
          const file = new File([blob], item.name, { type: item.type || blob.type });
          const data = new FormData();
          data.append('file', file);
          const res = await fetch(`${apiBase}/uploads`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
            body: data,
          });
          const json = await res.json();
          if (!res.ok) throw new Error(json.message || 'Upload failed');

          uploadedByPendingId[item.id] = json.url;
          uploadedKeys.push(json.key);
        }
        setUploadingImage(false);
      }

      const images = [
        ...(formData.images || []),
        ...pending.map(p => uploadedByPendingId[p.id]),
      ];
      const primary = formData.image.startsWith('pending:')
        ? uploadedByPendingId[formData.image.slice('pending:'.length)]
        : formData.image;

      // Drop blank rows the operator added but never filled in, then validate
      // what remains — a variant without a price cannot be sold.
      const variants = (formData.variants || [])
        .filter(v => String(v.label).trim() || String(v.price).trim())
        .map(v => ({
          label: String(v.label).trim(),
          price: parseFloat(v.price),
          stock: parseInt(v.stock, 10) || 0,
        }));

      const invalid = variants.find(v => !v.label || Number.isNaN(v.price));
      if (invalid) {
        throw new Error('Every option needs a label and a price');
      }

      const url = editingProduct
        ? `${apiBase}/products/${editingProduct._id}`
        : `${apiBase}/products`;

      const method = editingProduct ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...formData,
          images,
          image: primary || images[0] || '',
          variants,
          // For a product with options these are recalculated server-side from
          // the variants; sending sane values keeps validation happy either way.
          price: variants.length ? Math.min(...variants.map(v => v.price)) : parseFloat(formData.price),
          stock: variants.length
            ? variants.reduce((sum, v) => sum + v.stock, 0)
            : parseInt(formData.stock)
        })
      });

      if (response.ok) {
        setMessage(`✓ Product ${editingProduct ? 'updated' : 'added'} successfully!`);

        await clearDraft(draftScope, pending.map(p => p.id));
        Object.values(previewUrls.current).forEach(URL.revokeObjectURL);
        previewUrls.current = {};
        setPending([]);
        setDraftRestored(false);

        if (!editingProduct) {
          setFormData({
            name: '',
            description: '',
            price: '',
            category: 'Athletic Tapes',
            stock: '',
            image: '',
            images: [],
      variants: []
          });
        }
        setTimeout(() => {
          onProductSaved && onProductSaved();
        }, 1500);
      } else {
        const error = await response.json();
        await deleteUploaded(uploadedKeys);
        setMessage('✗ Failed: ' + (error.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error:', error);
      await deleteUploaded(uploadedKeys);
      setMessage('✗ Error: ' + error.message);
    } finally {
      setUploadingImage(false);
      setLoading(false);
    }
  };

  const discardDraft = async () => {
    await clearDraft(draftScope, pending.map(p => p.id));
    Object.values(previewUrls.current).forEach(URL.revokeObjectURL);
    previewUrls.current = {};
    setPending([]);
    setDraftRestored(false);
    setFormData(editingProduct ? {
      name: editingProduct.name,
      description: editingProduct.description,
      price: editingProduct.price,
      category: editingProduct.category,
      stock: editingProduct.stock,
      image: editingProduct.image,
      images: editingProduct.images || [],
      variants: editingProduct.variants || []
    } : {
      name: '', description: '', price: '', category: 'Athletic Tapes',
      stock: '', image: '', images: [], variants: []
    });
    setMessage('Draft discarded');
  };

  return (
    <div className="bg-white border border-gray-200 p-8">
      <h2 className="text-3xl font-light mb-8 tracking-tight">{editingProduct ? 'Edit Product' : 'Add New Product'}</h2>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Form Section */}
        <div className="lg:col-span-2">
          {message && (
            <div className={`p-4 mb-6 border ${message.startsWith('✓') ? 'bg-white border-black text-black' : 'bg-black text-white border-black'} font-light`}>
              {message}
            </div>
          )}

          {draftRestored && (
            <div className="p-4 mb-6 border border-gray-300 bg-gray-50 font-light text-sm flex items-center justify-between gap-4">
              <span>Restored unsaved changes from your last session.</span>
              <button type="button" onClick={discardDraft} className="text-red-600 hover:text-red-800 underline whitespace-nowrap">
                Discard draft
              </button>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <label className="block text-sm font-light text-black mb-3 tracking-wide">Product Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full border border-gray-300 px-6 py-3 focus:outline-none focus:border-black text-black font-light placeholder-gray-500"
                  placeholder="e.g., Premium Kinesiology Tape"
                />
              </div>

              <div>
                <label className="block text-sm font-light text-black mb-3 tracking-wide">Category *</label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  className="w-full border border-gray-300 px-6 py-3 focus:outline-none focus:border-black text-black font-light"
                >
                  <option>Athletic Tapes</option>
                  <option>Medical Grade</option>
                  <option>Support & Recovery</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-light text-black mb-3 tracking-wide">
                  Price (RM) {hasVariants ? '' : '*'}
                </label>
                <input
                  type="number"
                  name="price"
                  value={hasVariants ? Math.min(...variantPrices) : formData.price}
                  onChange={handleChange}
                  required={!hasVariants}
                  disabled={hasVariants}
                  step="0.01"
                  className="w-full border border-gray-300 px-6 py-3 focus:outline-none focus:border-black text-black font-light placeholder-gray-500 disabled:bg-gray-100 disabled:text-gray-500"
                  placeholder="99.99"
                />
                {hasVariants && <p className="text-xs text-gray-500 font-light mt-2">From the cheapest option.</p>}
              </div>

              <div>
                <label className="block text-sm font-light text-black mb-3 tracking-wide">
                  Stock Quantity {hasVariants ? '' : '*'}
                </label>
                <input
                  type="number"
                  name="stock"
                  value={hasVariants ? variantStockTotal : formData.stock}
                  onChange={handleChange}
                  required={!hasVariants}
                  disabled={hasVariants}
                  className="w-full border border-gray-300 px-6 py-3 focus:outline-none focus:border-black text-black font-light placeholder-gray-500 disabled:bg-gray-100 disabled:text-gray-500"
                  placeholder="50"
                />
                {hasVariants && <p className="text-xs text-gray-500 font-light mt-2">Total across all options.</p>}
              </div>
            </div>

            {/* Variants — optional. Adding any switches pricing and stock over
                to them, so the fields above become read-only summaries. */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-light text-black tracking-wide">
                  Options <span className="text-gray-500">(size, colour, …)</span>
                </label>
                <button type="button" onClick={addVariant}
                  className="text-sm font-light underline text-gray-700 hover:text-black">
                  + Add option
                </button>
              </div>

              {!hasVariants ? (
                <p className="text-xs text-gray-500 font-light border border-dashed border-gray-300 px-4 py-4">
                  No options — this product is sold as a single item at the price above.
                  Add options if it comes in sizes, colours or similar, each with its own price.
                </p>
              ) : (
                <div className="space-y-3">
                  <div className="hidden sm:grid grid-cols-12 gap-3 text-xs text-gray-500 font-light px-1">
                    <span className="col-span-6">Label</span>
                    <span className="col-span-3">Price (RM)</span>
                    <span className="col-span-2">Stock</span>
                  </div>
                  {formData.variants.map((variant, i) => (
                    <div key={i} className="grid grid-cols-12 gap-3 items-center">
                      <input
                        type="text"
                        value={variant.label}
                        onChange={(e) => updateVariant(i, 'label', e.target.value)}
                        placeholder="e.g. 5cm — Beige"
                        className="col-span-12 sm:col-span-6 border border-gray-300 px-4 py-2.5 font-light focus:outline-none focus:border-black"
                      />
                      <input
                        type="number"
                        value={variant.price}
                        onChange={(e) => updateVariant(i, 'price', e.target.value)}
                        step="0.01"
                        min="0"
                        placeholder="24.90"
                        className="col-span-5 sm:col-span-3 border border-gray-300 px-4 py-2.5 font-light focus:outline-none focus:border-black"
                      />
                      <input
                        type="number"
                        value={variant.stock}
                        onChange={(e) => updateVariant(i, 'stock', e.target.value)}
                        min="0"
                        placeholder="10"
                        className="col-span-5 sm:col-span-2 border border-gray-300 px-4 py-2.5 font-light focus:outline-none focus:border-black"
                      />
                      <button type="button" onClick={() => removeVariant(i)} title="Remove option"
                        className="col-span-2 sm:col-span-1 text-red-500 hover:text-red-700 text-lg">
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-light text-black mb-3 tracking-wide">Product Images</label>
              <div className="space-y-4">
                {/* Upload button */}
                <label className="flex items-center gap-3 cursor-pointer w-fit bg-black text-white hover:bg-gray-900 px-5 py-3 font-light tracking-wide transition-all duration-300">
                  <input type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden" onChange={handleImageUpload} disabled={uploadingImage || processingImages || loading} />
                  {processingImages ? 'Compressing…' : uploadingImage ? 'Uploading…' : '↑ Choose Images'}
                </label>
                <p className="text-xs text-gray-500 font-light">
                  JPEG, PNG or WebP · anything over 5 MB is compressed automatically · first image or starred image is the primary
                  {pending.length > 0 && ' · new images are stored when you save'}
                </p>

                {/* Thumbnail grid — saved images and not-yet-saved files together */}
                {galleryItems.length > 0 && (
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                    {galleryItems.map((item, i) => (
                      <div key={item.ref} className={`relative group rounded-lg overflow-hidden border-2 transition-all ${formData.image === item.ref ? 'border-black' : 'border-gray-200 hover:border-gray-400'}`}>
                        <img src={item.src} alt={`Product ${i+1}`} className="w-full aspect-square object-cover" />
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                          {formData.image !== item.ref && (
                            <button type="button" onClick={() => setPrimary(item.ref)} title="Set as primary"
                              className="bg-white text-black text-xs px-2 py-1 rounded font-light">★</button>
                          )}
                          <button type="button" onClick={() => removeImage(item.ref)} title="Remove"
                            className="bg-red-500 text-white text-xs px-2 py-1 rounded font-light">✕</button>
                        </div>
                        {formData.image === item.ref && (
                          <div className="absolute top-1 left-1 bg-black text-white text-xs px-1.5 py-0.5 rounded font-light">Primary</div>
                        )}
                        {item.pending && (
                          <div className="absolute bottom-1 right-1 bg-white/90 text-black text-xs px-1.5 py-0.5 rounded font-light">Not saved</div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Fallback URL input — only when no images are attached at all.
                    Kept as type="text": uploaded images are stored as relative
                    paths, which type="url" rejects as malformed and would block
                    the form from submitting. */}
                {galleryItems.length === 0 && (
                  <input
                    type="text"
                    name="image"
                    value={formData.image}
                    onChange={handleChange}
                    className="w-full border border-gray-300 px-6 py-3 focus:outline-none focus:border-black text-black font-light placeholder-gray-500"
                    placeholder="Or paste a primary image URL"
                  />
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-light text-black mb-3 tracking-wide">Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows="4"
                className="w-full border border-gray-300 px-6 py-3 focus:outline-none focus:border-black text-black font-light placeholder-gray-500"
                placeholder="Describe the product..."
              />
            </div>

            <div className="flex gap-4 pt-4">
              <button
                type="submit"
                disabled={loading}
                className="bg-black text-white hover:bg-gray-900 disabled:bg-gray-400 px-8 py-4 font-light tracking-wide transition-all duration-300"
              >
                {loading ? 'Processing...' : (editingProduct ? 'Update Product' : 'Add Product')}
              </button>
              <button
                type="reset"
                className="bg-white text-black border border-gray-300 hover:border-black px-8 py-4 font-light tracking-wide transition-all duration-300"
              >
                Clear Form
              </button>
            </div>
          </form>
        </div>

        {/* Preview Section */}
        <div className="lg:col-span-1">
          <div className="sticky top-8 bg-white border border-gray-200 p-6">
            <h3 className="text-lg font-light text-black mb-6 tracking-tight">Preview</h3>
            
            <div className="border border-gray-200">
              {/* Product Image */}
              <div className="bg-gray-100 h-48 flex items-center justify-center overflow-hidden">
                {primaryPreviewSrc ? (
                  <img
                    src={primaryPreviewSrc}
                    alt={formData.name || 'Product'}
                    className="w-full h-full object-cover"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                ) : (
                  <span className="text-gray-400 font-light">No image</span>
                )}
              </div>
              {galleryItems.length > 1 && (
                <div className="flex gap-1 p-2 bg-gray-50 border-t border-gray-100 overflow-x-auto">
                  {galleryItems.map((item) => (
                    <img key={item.ref} src={item.src} alt="" className={`h-10 w-10 object-cover rounded flex-shrink-0 border-2 ${formData.image === item.ref ? 'border-black' : 'border-transparent'}`} />
                  ))}
                </div>
              )}

              {/* Product Info */}
              <div className="p-4 space-y-3">
                <h4 className="text-base font-light text-black truncate">
                  {formData.name || 'Product Name'}
                </h4>
                
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-xs text-gray-600 font-light mb-1">Category</p>
                    <p className="text-sm font-light text-black">
                      {formData.category || 'N/A'}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-600 font-light mb-1">Stock</p>
                    <p className="text-sm font-light text-black">
                      {formData.stock || '0'}
                    </p>
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-3">
                  <p className="text-xs text-gray-600 font-light mb-1">Price</p>
                  <p className="text-lg font-light text-black">
                    {getCurrencySymbol('MYR')} {parseFloat(formData.price || 0).toFixed(2)}
                  </p>
                </div>

                <div className="border-t border-gray-200 pt-3">
                  <p className="text-xs text-gray-600 font-light mb-1">Description</p>
                  <p className="text-xs font-light text-gray-700 line-clamp-3">
                    {formData.description || 'No description yet'}
                  </p>
                </div>

                <button className="w-full bg-black text-white py-2 hover:bg-gray-900 transition-all font-light text-sm mt-4">
                  Add to Cart
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Guided fulfilment sequence for one order.
 *
 * Each step reports whether it is done and, when it is the current one, renders
 * the single action that advances it. Steps stay visible once complete so a
 * message that was never sent can still be sent afterwards — the WhatsApp links
 * are recomputed server-side on every load rather than shown once.
 */
function OrderStepper({ order, busy, onConfirmPayment, onOpenTracking, onNotified, onSetStatus, receiptHref }) {
  const waButton = (href, label, onClick) => href ? (
    <a href={href} target="_blank" rel="noopener noreferrer" onClick={onClick}
      className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#20bd5a] text-white px-4 py-2 text-sm font-medium transition-colors">
      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347" />
      </svg>
      {label}
    </a>
  ) : (
    <span className="text-xs text-red-600 font-light">No usable WhatsApp number on this order</span>
  );

  const sentNote = (at) => at
    ? <span className="text-xs text-gray-500 font-light">Opened {new Date(at).toLocaleString()}</span>
    : null;

  const steps = [
    {
      key: 'placed',
      label: 'Order placed',
      done: true,
      detail: `${order.orderNumber || ''} · ${new Date(order.createdAt).toLocaleString()}`,
    },
    {
      key: 'payment_details',
      label: 'Send payment details',
      done: Boolean(order.paymentDetailsSentAt) || order.paymentStatus === 'completed',
      optional: true,
      detail: 'Only needed if the customer asks again — the details are already on their order page.',
      action: order.paymentStatus !== 'completed' && (
        <div className="flex flex-wrap items-center gap-3">
          {waButton(order.paymentWhatsappUrl, 'Send payment details', () => onNotified('payment'))}
          {sentNote(order.paymentDetailsSentAt)}
        </div>
      ),
    },
    {
      key: 'verify',
      label: 'Verify payment',
      done: order.paymentStatus === 'completed',
      detail: order.paymentStatus === 'completed'
        ? `Receipt ${order.receiptNumber || ''} issued${order.paymentReference ? ` · ref ${order.paymentReference}` : ''}${order.paidAt ? ` · ${new Date(order.paidAt).toLocaleString()}` : ''}`
        : 'Check the transfer proof the customer sent on WhatsApp, then confirm.',
      action: order.paymentStatus !== 'completed' ? (
        <button onClick={onConfirmPayment} disabled={busy}
          className="bg-black text-white px-5 py-2 text-sm font-light hover:bg-gray-900 disabled:opacity-50 transition-colors">
          {busy ? 'Working…' : '✓ Confirm payment received'}
        </button>
      ) : (
        // The receipt is issued here but not sent yet — it goes out with the
        // tracking details so the customer gets one message, not two.
        <a href={receiptHref} target="_blank" rel="noopener noreferrer"
          className="underline text-sm font-light text-gray-700 hover:text-black">View receipt</a>
      ),
    },
    {
      key: 'packed',
      label: 'Pack the order',
      done: ['packed', 'shipped', 'delivered'].includes(order.status),
      blocked: order.paymentStatus !== 'completed',
      detail: 'Mark once the parcel is ready for the courier.',
      action: order.paymentStatus === 'completed' && !['packed', 'shipped', 'delivered'].includes(order.status) && (
        <button onClick={() => onSetStatus('packed')} disabled={busy}
          className="border border-black px-5 py-2 text-sm font-light hover:bg-black hover:text-white disabled:opacity-50 transition-colors">
          Mark as packed
        </button>
      ),
    },
    {
      key: 'ship',
      label: 'Add courier & tracking',
      done: Boolean(order.trackingNumber),
      blocked: order.paymentStatus !== 'completed',
      detail: order.trackingNumber
        ? `${order.courier || 'Courier'} · ${order.trackingNumber}`
        : 'Courier and tracking number are both required.',
      action: order.paymentStatus === 'completed' && (
        <button onClick={onOpenTracking} disabled={busy}
          className="border border-black px-5 py-2 text-sm font-light hover:bg-black hover:text-white disabled:opacity-50 transition-colors">
          {order.trackingNumber ? '✎ Update tracking' : '⇢ Add tracking & mark shipped'}
        </button>
      ),
    },
    {
      key: 'tracking_sent',
      label: 'Send receipt & tracking to customer',
      done: Boolean(order.trackingSentAt),
      blocked: !order.trackingNumber,
      detail: order.trackingNumber
        ? 'One message: receipt link, courier and tracking number.'
        : 'Available once tracking is added.',
      action: order.trackingNumber && (
        <div className="flex flex-wrap items-center gap-3">
          {waButton(order.trackingWhatsappUrl, order.trackingSentAt ? 'Send again' : 'Send receipt & tracking', () => onNotified('tracking'))}
          {sentNote(order.trackingSentAt)}
        </div>
      ),
    },
    {
      key: 'delivered',
      label: 'Delivered',
      done: order.status === 'delivered',
      blocked: order.status !== 'shipped' && order.status !== 'delivered',
      detail: 'Close the order once the customer confirms receipt.',
      action: order.status === 'shipped' && (
        <button onClick={() => onSetStatus('delivered')} disabled={busy}
          className="border border-black px-5 py-2 text-sm font-light hover:bg-black hover:text-white disabled:opacity-50 transition-colors">
          Mark as delivered
        </button>
      ),
    },
  ];

  // The first incomplete, unblocked step is what the operator should do now.
  const currentIndex = steps.findIndex(s => !s.done && !s.blocked && s.action);

  if (order.status === 'cancelled') {
    return (
      <div className="border-b border-gray-300 pb-4">
        <p className="text-sm font-light text-red-700">This order was cancelled.</p>
      </div>
    );
  }

  return (
    <div className="border-b border-gray-300 pb-6">
      <div className="flex items-center justify-between mb-4">
        <h4 className="text-sm font-light text-black tracking-wide">Fulfilment</h4>
        <a href={`/order/${order.publicToken}`} target="_blank" rel="noopener noreferrer"
          className="underline text-xs font-light text-gray-600 hover:text-black">Customer view</a>
      </div>

      <ol className="space-y-0">
        {steps.map((step, i) => {
          const current = i === currentIndex;
          return (
            <li key={step.key} className="flex gap-4">
              {/* rail */}
              <div className="flex flex-col items-center">
                <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-medium flex-shrink-0 ${
                  step.done ? 'bg-black text-white'
                    : current ? 'bg-white text-black ring-2 ring-black'
                      : 'bg-gray-200 text-gray-500'
                }`}>
                  {step.done ? '✓' : i + 1}
                </span>
                {i < steps.length - 1 && (
                  <span className={`w-px flex-1 my-1 ${step.done ? 'bg-black' : 'bg-gray-200'}`} />
                )}
              </div>

              <div className={`pb-5 flex-grow min-w-0 ${step.blocked && !step.done ? 'opacity-40' : ''}`}>
                <p className={`text-sm ${current ? 'font-medium text-black' : step.done ? 'font-light text-gray-700' : 'font-light text-gray-500'}`}>
                  {step.label}
                  {step.optional && !step.done && <span className="text-xs text-gray-400 font-light"> · optional</span>}
                </p>
                {step.detail && <p className="text-xs text-gray-500 font-light mt-0.5">{step.detail}</p>}
                {step.action && <div className="mt-2">{step.action}</div>}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function AdminOrders() {
  const [orders, setOrders] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [expandedOrder, setExpandedOrder] = React.useState(null);
  const [updatingOrderId, setUpdatingOrderId] = React.useState(null);
  const [filterStatus, setFilterStatus] = React.useState('all');
  // Holds the WhatsApp reply link produced by the last fulfilment action.
  // Non-null while the courier/tracking dialog is open.
  const [trackingForm, setTrackingForm] = React.useState(null);
  const { getCurrencySymbol } = useCurrency();

  React.useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await fetch(`${API_BASE}/orders`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        console.log('Fetched orders:', data.length, 'orders');
        setOrders(data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
      } else {
        console.error('Failed to fetch orders:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    setUpdatingOrderId(orderId);
    try {
      const response = await fetch(`${API_BASE}/orders/${orderId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        // Refetch rather than merge the response: only the list endpoint
        // computes the WhatsApp links the stepper needs, so splicing in a raw
        // order document would strip them.
        await fetchOrders();
      } else {
        const err = await response.json().catch(() => ({}));
        alert(err.message || 'Failed to update status');
      }
    } catch (error) {
      console.error('Error updating order status:', error);
    } finally {
      setUpdatingOrderId(null);
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'delivered':
        return 'bg-white border border-black text-black';
      case 'shipped':
        return 'bg-blue-50 text-blue-900 border border-blue-200';
      case 'paid':
      case 'packed':
      case 'confirmed':
        return 'bg-green-50 text-green-900 border border-green-200';
      case 'payment_review':
        return 'bg-orange-50 text-orange-900 border border-orange-300';
      case 'awaiting_payment':
      case 'pending':
        return 'bg-yellow-50 text-yellow-900 border border-yellow-200';
      case 'cancelled':
        return 'bg-red-50 text-red-900 border border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border border-gray-300';
    }
  };

  // Confirm a bank transfer after checking the proof sent over WhatsApp. Issues
  // the receipt and hands back a prefilled reply for the customer.
  const confirmPayment = async (order) => {
    const reference = window.prompt(
      `Confirm payment for ${order.orderNumber || 'this order'}?\n\nOptional: bank reference from the transfer proof.`,
      ''
    );
    if (reference === null) return; // cancelled

    setUpdatingOrderId(order._id);
    try {
      const res = await fetch(`${API_BASE}/orders/${order._id}/confirm-payment`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ paymentReference: reference })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to confirm payment');

      await fetchOrders(); // canonical shape, including the computed links
    } catch (err) {
      alert(err.message);
    } finally {
      setUpdatingOrderId(null);
    }
  };

  // Records that the operator opened a prefilled WhatsApp message, which is the
  // only signal we get that a customer was contacted.
  const markNotified = async (order, type) => {
    const field = { payment: 'paymentDetailsSentAt', receipt: 'receiptSentAt', tracking: 'trackingSentAt' }[type];
    // Optimistic: the link opens in a new tab either way, and a wrong "sent"
    // mark is cheap to recover from — the message can always be sent again.
    setOrders(prev => prev.map(o => o._id === order._id ? { ...o, [field]: new Date().toISOString() } : o));
    try {
      await fetch(`${API_BASE}/orders/${order._id}/notified`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ type })
      });
    } catch {
      /* the stepper still advances locally; a reload will correct it */
    }
  };

  const setStatus = (order, status) => updateOrderStatus(order._id, status);

  // Marking an order shipped always goes through this dialog, so a shipment can
  // never be recorded without a courier and tracking number.
  const openTrackingModal = (order) => {
    const known = COURIERS.includes(order.courier);
    setTrackingForm({
      order,
      courier: order.courier ? (known ? order.courier : 'Other') : '',
      courierOther: order.courier && !known ? order.courier : '',
      trackingNumber: order.trackingNumber || '',
      error: '',
      saving: false,
    });
  };

  const submitTracking = async (e) => {
    e.preventDefault();
    const { order, courier, courierOther, trackingNumber } = trackingForm;
    const resolvedCourier = (courier === 'Other' ? courierOther : courier).trim();

    if (!resolvedCourier) {
      return setTrackingForm(f => ({ ...f, error: 'Select a courier.' }));
    }
    if (!trackingNumber.trim()) {
      return setTrackingForm(f => ({ ...f, error: 'Enter a tracking number.' }));
    }

    setTrackingForm(f => ({ ...f, saving: true, error: '' }));
    try {
      const res = await fetch(`${API_BASE}/orders/${order._id}/tracking`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ trackingNumber: trackingNumber.trim(), courier: resolvedCourier })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to save tracking');

      await fetchOrders();
      setExpandedOrder(order._id); // reveal the next step in the stepper
      setTrackingForm(null);
    } catch (err) {
      setTrackingForm(f => ({ ...f, saving: false, error: err.message }));
    }
  };

  const getPaymentStatusColor = (status) => {
    switch(status) {
      case 'completed':
        return 'text-green-700';
      case 'pending':
        return 'text-yellow-700';
      case 'failed':
        return 'text-red-700';
      default:
        return 'text-gray-700';
    }
  };

  const filteredOrders = filterStatus === 'all' 
    ? orders 
    : orders.filter(o => o.status === filterStatus);

  if (loading) return <div className="text-center py-12 font-light text-gray-600">Loading orders...</div>;

  return (
    <div className="bg-white border border-gray-200 p-8 h-full flex flex-col overflow-hidden">
      <div className="flex justify-between items-center mb-8 flex-shrink-0">
        <div>
          <h2 className="text-3xl font-light tracking-tight">Orders</h2>
          <p className="text-xs text-gray-600 mt-2 font-light">Total: {orders.length} | Filtered: {filteredOrders.length}</p>
        </div>
        <div className="flex gap-3 flex-wrap">
          {['all', 'awaiting_payment', 'payment_review', 'paid', 'packed', 'shipped', 'delivered', 'cancelled'].map(status => (
            <button
              key={status}
              onClick={() => setFilterStatus(status)}
              className={`px-4 py-2 text-sm font-light border transition-all ${
                filterStatus === status
                  ? 'bg-black text-white border-black'
                  : 'bg-white text-black border-gray-300 hover:border-black'
              }`}
            >
              {status.replace(/_/g, ' ').replace(/^./, c => c.toUpperCase())} {filterStatus === status && `(${filteredOrders.length})`}
            </button>
          ))}
        </div>
      </div>

      {filteredOrders.length === 0 ? (
        <p className="text-gray-600 font-light">No orders found</p>
      ) : (
        <div className="flex-grow overflow-y-auto border border-gray-200 p-4">
          <div className="space-y-4">
          {filteredOrders.map(order => (
            <div key={order._id} className="border border-gray-200 overflow-hidden">
              <div className="p-6 hover:bg-gray-50 cursor-pointer transition-all" onClick={() => setExpandedOrder(expandedOrder === order._id ? null : order._id)}>
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-center">
                  <div>
                    <p className="text-xs text-gray-600 font-light mb-1">Order</p>
                    <p className="font-mono text-sm text-black font-light">{order.orderNumber || order._id?.slice(-8).toUpperCase()}</p>
                  </div>

                  <div>
                    <p className="text-xs text-gray-600 font-light mb-1">Customer</p>
                    <p className="text-sm text-black font-light">{order.guestName || order.userId?.name || 'Guest'}</p>
                  </div>

                  <div>
                    <p className="text-xs text-gray-600 font-light mb-1">Total</p>
                    <p className="text-sm text-black font-light">{getCurrencySymbol('MYR')}{order.totalPrice.toFixed(2)}</p>
                  </div>

                  <div>
                    <p className="text-xs text-gray-600 font-light mb-1">Status</p>
                    <select
                      value={order.status}
                      onChange={(e) => e.target.value === 'shipped'
                        ? openTrackingModal(order)   // must capture tracking first
                        : updateOrderStatus(order._id, e.target.value)}
                      disabled={updatingOrderId === order._id}
                      onClick={(e) => e.stopPropagation()}
                      className={`text-sm font-light px-3 py-1 border cursor-pointer ${getStatusColor(order.status)} disabled:opacity-50`}
                    >
                      <option value="awaiting_payment">Awaiting payment</option>
                      <option value="payment_review">Verifying payment</option>
                      <option value="paid">Paid</option>
                      <option value="packed">Packed</option>
                      <option value="shipped">Shipped</option>
                      <option value="delivered">Delivered</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>

                  <div>
                    <p className="text-xs text-gray-600 font-light mb-1">Date</p>
                    <p className="text-sm text-black font-light">{new Date(order.createdAt).toLocaleDateString()}</p>
                  </div>
                </div>
              </div>

              {/* Expandable Details */}
              {expandedOrder === order._id && (
                <div className="bg-gray-50 border-t border-gray-200 p-6 space-y-6">

                  {/* Fulfilment stepper: what has happened, what to do next.
                      Every prefilled message stays reachable, so a step that
                      was skipped can always be picked up later. */}
                  <OrderStepper
                    order={order}
                    busy={updatingOrderId === order._id}
                    onConfirmPayment={() => confirmPayment(order)}
                    onOpenTracking={() => openTrackingModal(order)}
                    onNotified={(type) => markNotified(order, type)}
                    onSetStatus={(status) => setStatus(order, status)}
                    receiptHref={`${API_BASE.replace(/\/api\/?$/, '')}/receipt/${order.publicToken}`}
                  />

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <h4 className="text-sm font-light text-black mb-3 tracking-wide border-b border-gray-300 pb-2">Payment Info</h4>
                      <div className="space-y-2">
                        <p className="text-xs text-gray-600 font-light">Payment Status</p>
                        <p className={`text-sm font-light ${getPaymentStatusColor(order.paymentStatus)}`}>
                          {order.paymentStatus?.charAt(0).toUpperCase() + order.paymentStatus?.slice(1)}
                        </p>
                        {order.trackingNumber && (
                          <p className="text-xs text-gray-700 font-light pt-2">
                            {order.courier || 'Courier'}: <span className="font-medium">{order.trackingNumber}</span>
                          </p>
                        )}
                      </div>
                    </div>

                    <div>
                      <h4 className="text-sm font-light text-black mb-3 tracking-wide border-b border-gray-300 pb-2">Shipping Address</h4>
                      <div className="space-y-1 text-xs font-light text-gray-700">
                        <p>{order.shippingAddress?.street}</p>
                        <p>{order.shippingAddress?.city}, {order.shippingAddress?.state} {order.shippingAddress?.zip}</p>
                        <p>{order.shippingAddress?.country}</p>
                      </div>
                    </div>

                    <div>
                      <h4 className="text-sm font-light text-black mb-3 tracking-wide border-b border-gray-300 pb-2">Contact</h4>
                      <div className="space-y-2 text-xs font-light text-gray-700">
                        <p><strong>Email:</strong> {order.guestEmail || order.userId?.email || 'N/A'}</p>
                        <p><strong>Phone:</strong> {order.guestPhone || 'N/A'}</p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-sm font-light text-black mb-3 tracking-wide border-b border-gray-300 pb-2">Order Items</h4>
                    <div className="space-y-2">
                      {order.items.map((item, idx) => (
                        <div key={idx} className="flex justify-between text-xs font-light text-gray-700 py-2 border-b border-gray-200 last:border-0">
                          <span>{item.productId?.name || 'Product'} x {item.quantity}</span>
                          <span>{getCurrencySymbol('MYR')}{(item.price * item.quantity).toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {order.trackingNumber && (
                    <div>
                      <h4 className="text-sm font-light text-black mb-2">Tracking Number</h4>
                      <p className="font-mono text-sm bg-white border border-gray-300 px-4 py-2">{order.trackingNumber}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
          </div>
        </div>
      )}

      {/* Courier + tracking dialog. The only route to 'shipped'. */}
      {trackingForm && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          onClick={() => !trackingForm.saving && setTrackingForm(null)}
        >
          <form
            onSubmit={submitTracking}
            onClick={(e) => e.stopPropagation()}
            className="bg-white w-full max-w-md p-8 space-y-5"
          >
            <div>
              <h3 className="text-2xl font-light tracking-tight">Mark as shipped</h3>
              <p className="text-sm text-gray-600 font-light mt-1">
                Order {trackingForm.order.orderNumber || trackingForm.order._id?.slice(-8).toUpperCase()}
              </p>
            </div>

            <div>
              <label className="block text-sm font-light text-black mb-2 tracking-wide">Courier *</label>
              <select
                value={trackingForm.courier}
                onChange={(e) => setTrackingForm(f => ({ ...f, courier: e.target.value, error: '' }))}
                required
                autoFocus
                className="w-full border border-gray-300 px-4 py-3 font-light focus:outline-none focus:border-black"
              >
                <option value="" disabled>Select a courier…</option>
                {COURIERS.map(name => <option key={name} value={name}>{name}</option>)}
              </select>
            </div>

            {trackingForm.courier === 'Other' && (
              <div>
                <label className="block text-sm font-light text-black mb-2 tracking-wide">Courier name *</label>
                <input
                  type="text"
                  value={trackingForm.courierOther}
                  onChange={(e) => setTrackingForm(f => ({ ...f, courierOther: e.target.value, error: '' }))}
                  placeholder="e.g. ABX Express"
                  className="w-full border border-gray-300 px-4 py-3 font-light focus:outline-none focus:border-black"
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-light text-black mb-2 tracking-wide">Tracking number *</label>
              <input
                type="text"
                value={trackingForm.trackingNumber}
                onChange={(e) => setTrackingForm(f => ({ ...f, trackingNumber: e.target.value, error: '' }))}
                placeholder="e.g. MY123456789"
                className="w-full border border-gray-300 px-4 py-3 font-mono text-sm focus:outline-none focus:border-black"
              />
              <p className="text-xs text-gray-500 font-light mt-2">
                Sent to the customer, so check it before saving.
              </p>
            </div>

            {trackingForm.error && (
              <p className="text-sm text-red-600 font-light">{trackingForm.error}</p>
            )}

            <div className="flex gap-3 pt-2">
              <button
                type="submit"
                disabled={trackingForm.saving}
                className="flex-grow bg-black text-white py-3 font-light tracking-wide hover:bg-gray-900 disabled:opacity-50 transition-colors"
              >
                {trackingForm.saving ? 'Saving…' : 'Save & mark shipped'}
              </button>
              <button
                type="button"
                onClick={() => setTrackingForm(null)}
                disabled={trackingForm.saving}
                className="border border-gray-300 px-5 py-3 font-light hover:border-black disabled:opacity-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function AdminUsers() {
  const [users, setUsers] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await fetch(`${API_BASE}/users`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        if (response.ok) {
          const data = await response.json();
          setUsers(Array.isArray(data) ? data : []);
        }
      } catch (error) {
        console.error('Error fetching users:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, []);

  if (loading) return <div className="text-center py-12 font-light text-gray-600">Loading users...</div>;

  return (
    <div className="bg-white border border-gray-200 p-8 h-full flex flex-col overflow-hidden">
      <h2 className="text-3xl font-light mb-8 tracking-tight">Users</h2>
      {users.length === 0 ? (
        <p className="text-gray-600 font-light">No users found</p>
      ) : (
        <div className="flex-grow overflow-y-auto border border-gray-200 rounded">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-light text-gray-700 tracking-wide">Name</th>
                  <th className="px-6 py-4 text-left text-sm font-light text-gray-700 tracking-wide">Email</th>
                  <th className="px-6 py-4 text-left text-sm font-light text-gray-700 tracking-wide">Role</th>
                  <th className="px-6 py-4 text-left text-sm font-light text-gray-700 tracking-wide">Joined</th>
                </tr>
              </thead>
              <tbody>
                {users.map(user => (
                  <tr key={user._id} className="border-b border-gray-200 hover:bg-gray-50">
                    <td className="px-6 py-4 text-gray-900 font-light">{user.firstName} {user.lastName}</td>
                    <td className="px-6 py-4 text-gray-700 font-light">{user.email}</td>
                    <td className="px-6 py-4">
                      <span className={`text-xs font-light px-3 py-1 border ${user.role === 'admin' ? 'bg-black text-white border-black' : 'bg-white text-gray-700 border-gray-300'}`}>
                        {user.role}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-gray-700 font-light">{new Date(user.createdAt).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
